import Vue from "vue";
import {
  GraphicComponentTypes,
  UGCData,
  PlacementTypes,
} from "@/libs/graphics/types/Graphics.t";

export interface UGCDataStore {
  componentID: PlacementTypes;
  subcomponentID: GraphicComponentTypes;
  ugcData: UGCData;
  outOfBoundsModalShown?: Boolean;
  zoomValue?: string;
}

// This keeps track of the last time the UGC data changes so we can
// let other vue components use it to "react" to changes in this UGCData
// store since it is not a Vuex store.
export let lastUpdated = new Vue({
  data: {
    timestamp: Date.now(),
  },
});

const data: UGCDataStore[] = [];

/**
 * It takes in a UGCDataStore object and adds it to the data array.
 * @param {UGCDataStore} ugcDataStoreObject - The UGCDataStore object that you want to store.
 */
export const setUGCData = (ugcDataStoreObject: UGCDataStore) => {
  const indexExists = data.findIndex(
    (item: UGCDataStore) =>
      item.componentID === ugcDataStoreObject.componentID &&
      item.subcomponentID === ugcDataStoreObject.subcomponentID
  );
  if (indexExists >= 0) data.splice(indexExists, 1);
  data.push(ugcDataStoreObject);
  lastUpdated.timestamp = Date.now();
};

/**
 * Given a component ID and a subcomponent ID, return the data for that component and subcomponent
 * @param {PlacementTypes} componentID - The type of component you want to get data for.
 * @param {GraphicComponentTypes} subcomponentID - The subcomponentID of the UGC component you want to
 * get data for.
 * @returns An object with the following structure:
 * ```
 * {
 *     componentID: PlacementTypes,
 *     subcomponentID: GraphicComponentTypes,
 *     data: {
 *         [key: string]: string
 *     }
 * }
 * ```
 */
export const getUGCDataByID = (
  componentID: PlacementTypes,
  subcomponentID: GraphicComponentTypes
): UGCDataStore | undefined => {
  const entry = data.find(
    (item: UGCDataStore) =>
      item.componentID === componentID && item.subcomponentID === subcomponentID
  );
  return entry
};

export const getUGCDataByImageURL = (
  original_image: string | undefined
): UGCDataStore | undefined => {
  const entry = data.find(
    (item: UGCDataStore) =>
      item.ugcData.original_image === original_image
  )
  return entry
}

/**
 * Get all the data from the UGC Data Store
 * @returns An array of objects
 */
export const getAllUGCData = () => {
  return data;
};
